import React, { useMemo, useState, useRef, useEffect } from 'react'
import { Row, Col } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { AccountSourcingFields, isSourcingField } from '../../../../../core/components/AccountSourcingFields.tsx'
import { useCurrencies } from '../../../../currencies/useCurrencies.ts'
import { useLookups } from '../../../../global_configs/hooks/useLookups.ts'
import { updateProspectRecord } from '../../../../../state_manager/state_update_actions/fundraising/index.ts'
import { updateActiveRecord } from '../../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { ProspectFields } from '../../../utils/fieldConfigs.ts'
import { getOverviewProspectInfoFields, getOverviewProspectTeamFields } from '../../../utils/fieldConfigs.ts'
import { fetchFundraisingById } from '../../../services/fundraisingService.ts'
import { SECTION_PROSPECT_OVERVIEW } from '../../../../../state_manager/constants/strings.ts'
import { PIPELINE_TYPE_FUNDRAISING } from '../../../../../state_manager/globals.ts'
import { UserEntryFields } from '../../../../../state_manager/data_models/user_entry.ts'
import { UNKNOWN_CONTACT } from '../../../../../state_manager/constants/strings.ts'
import { getAccessToken } from '../../../../../core/auth/authService'
import { store, type RootState } from '../../../../../state_manager/store.ts'
import {
  applyFundChangeToUpdates,
  getDefaultPipelineConfigForPipeline,
  normalizeFundIdFromPatch,
  syncFundChangePipelineOnServer
} from '../../../utils/pipelineFundStageRules.ts'
import { useFundFieldOptions } from '../../../../global_configs/hooks/useFundFieldOptions.tsx'
import { useStageMoveRights } from '../../../../../core/hooks/useStageMoveRights.ts'
import { contactOptionsFromFieldValue, type ProspectContactsByRole } from '../../../utils/prospectContacts.ts'

export function ProspectOverviewSection({
  prospect,
  formData,
  handleFieldChange,
  onPatchSuccess,
  selectedContacts = { introduced_by: [], sourced_by: [], lead: [] },
  onContactsChange,
  cardProps = {},
  isReadOnly = false,
  isDrawer = false,
  recordId = null
}: {
  prospect?: any | null
  formData?: Record<string, any>
  handleFieldChange?: (field: string, value: any) => void
  onPatchSuccess?: (patchRecordId: string, updates: Record<string, any>) => void
  selectedContacts?: ProspectContactsByRole
  onContactsChange?: (contacts: ProspectContactsByRole) => void
  cardProps?: any
  isReadOnly?: boolean
  isDrawer?: boolean
  recordId?: string | null
}) {
  const { getCurrencyById, getCurrencyOptions } = useCurrencies()
  const { detailsByCategory } = useLookups()

  const pipelines = useSelector((state: any) => state.globalConfigs?.pipelines || [])
  const pipelineConfigs = useSelector((state: RootState) => state.globalConfigs?.pipeline_configs || [])
  const pipelineStages = useSelector((state: RootState) => state.globalConfigs?.pipeline_stages || [])
  const fundOptions = useSelector((state: any) => state.globalConfigs.funds || [])

  const defaultFundraisingPipelineId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (pipeline: { default?: boolean; type?: string; id?: string }) =>
        pipeline.default === true && pipeline.type === PIPELINE_TYPE_FUNDRAISING
    ) as { id?: string } | undefined

    return defaultPipeline?.id || null
  }, [pipelines])

  const dispatch = useDispatch()

  const investorLookupId =
    detailsByCategory?.['Global Configs']?.find((option) => option.label === 'internal_company_id')?.value || null

  const [previousContacts, setPreviousContacts] = useState(() => {
    return {
      introduced_by: [...(selectedContacts.introduced_by || [])],
      sourced_by: [...(selectedContacts.sourced_by || [])],
      lead: [...(selectedContacts.lead || [])]
    }
  })

  const hasInitializedRef = useRef(false)

  useEffect(() => {
    const hasSelectedData =
      selectedContacts.introduced_by?.length > 0 ||
      selectedContacts.sourced_by?.length > 0 ||
      selectedContacts.lead?.length > 0
    const hasPreviousData =
      previousContacts.introduced_by?.length > 0 ||
      previousContacts.sourced_by?.length > 0 ||
      previousContacts.lead?.length > 0

    if ((hasSelectedData && !hasPreviousData) || (!hasInitializedRef.current && hasSelectedData)) {
      setPreviousContacts({
        introduced_by: [...(selectedContacts.introduced_by || [])],
        sourced_by: [...(selectedContacts.sourced_by || [])],
        lead: [...(selectedContacts.lead || [])]
      })
      hasInitializedRef.current = true
    }
  }, [selectedContacts.introduced_by?.length, selectedContacts.sourced_by?.length, selectedContacts.lead?.length])

  const data = useMemo(() => {
    const base = prospect || {}
    const overrides = formData || {}
    if (!prospect && !formData) return {}
    return { ...base, ...overrides }
  }, [prospect, formData])

  const prospectId =
    recordId || data[ProspectFields.prospects__id] || prospect?.[ProspectFields.prospects__id] || prospect?.id

  const fundIdForPipeline = data[ProspectFields.prospects__fund]
  const savedPipelineOrigination = (data as { prospects__pipeline_origination?: string | null })
    .prospects__pipeline_origination

  const prospectPipelineId = useMemo(() => {
    if (fundIdForPipeline && Array.isArray(fundOptions)) {
      const fundRow = fundOptions.find((f: { id?: string; value?: string }) => (f.id ?? f.value) === fundIdForPipeline)
      const po = fundRow?.pipeline_origination
      if (typeof po === 'string' && po.length > 0) return po
    }
    if (typeof savedPipelineOrigination === 'string' && savedPipelineOrigination.length > 0) {
      return savedPipelineOrigination
    }
    return defaultFundraisingPipelineId
  }, [fundIdForPipeline, savedPipelineOrigination, fundOptions, defaultFundraisingPipelineId])

  // Fund picker gate (fund status, 2026-09-04). scope 'fundraising': this
  // chooses a fund to RAISE INTO, so a closed raise does not belong in the
  // list. The row's own fund is pinned, so an existing prospect on a
  // since-closed fund still shows it.
  //
  // NOTE `fundOptions` above is the RAW funds array despite its name, and it
  // stays ungated — the pipeline-origination resolver reads through it, and a
  // gated-out fund must still resolve its pipeline.
  const { options: normalizedFundOptions, optionsFooter: fundOptionsFooter } = useFundFieldOptions({
    scope: 'fundraising',
    pin: fundIdForPipeline
  })

  const normalizedCurrencyOptions = useMemo(() => {
    const currencyOptions = getCurrencyOptions()
    if (!currencyOptions || !Array.isArray(currencyOptions)) return []
    return currencyOptions
      .map((option: any) => {
        const label = option.label || option.name || option.code || ''
        const value = option.value || option.id || option.code || ''
        return {
          value,
          label
        }
      })
      .filter((option: any) => option.label && option.value)
  }, [getCurrencyOptions])

  // Stages the user may not move this prospect into - or every other stage, when
  // they may not move it out of its current one - are disabled with the reason.
  const applyStageMoveRights = useStageMoveRights()
  const stageIdForRights = data[ProspectFields.pipeline_prospects__stage]

  const pipelineStageOptions = useMemo(() => {
    if (!prospectPipelineId || !pipelineConfigs.length || !pipelineStages.length) {
      return []
    }

    const filteredConfigs = pipelineConfigs.filter(
      (config) => String(config.pipeline) === String(prospectPipelineId) && config.visible_tbl === true
    )

    const allPipelineStageOptions = filteredConfigs
      .map((config) => {
        const stage = pipelineStages.find((s) => s.id === config.stage)
        return {
          value: config.id,
          label: stage?.name || '',
          sequence: config.sequence_tbl || config.sequence_kb || 0
        }
      })
      .filter((option) => option.label && typeof option.label === 'string')
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''))

    return applyStageMoveRights(allPipelineStageOptions, stageIdForRights)
  }, [prospectPipelineId, pipelineConfigs, pipelineStages, applyStageMoveRights, stageIdForRights])

  const pipelineStageIdsKey = useMemo(
    () =>
      pipelineStageOptions
        .map((o) => o.value)
        .filter(Boolean)
        .sort()
        .join('|'),
    [pipelineStageOptions]
  )

  const currentPipelineStageId = data[ProspectFields.pipeline_prospects__stage] ?? ''

  useEffect(() => {
    if (!currentPipelineStageId || !pipelineStageOptions.length) return
    const stageIsValid = pipelineStageOptions.some((o) => o.value === currentPipelineStageId)
    if (stageIsValid) return

    const reset = getDefaultPipelineConfigForPipeline(prospectPipelineId, pipelineConfigs, pipelineStages)

    if (reset) {
      if (isDrawer && handleFieldChange) {
        handleFieldChange(ProspectFields.pipeline_prospects__stage, reset.configId)
        handleFieldChange(ProspectFields.pipeline_prospects__stage__label, reset.label)
        return
      }

      if (!isDrawer && recordId && recordId !== 'new') {
        const resetUpdates = {
          [ProspectFields.pipeline_prospects__stage]: reset.configId,
          [ProspectFields.pipeline_prospects__stage__label]: reset.label,
          pipeline_config__id: reset.configId
        }
        dispatch(updateProspectRecord({ prospectId: recordId.toString(), updates: resetUpdates }))
        dispatch(updateActiveRecord({ uuid: recordId.toString(), updates: resetUpdates }))
      }
    } else if (isDrawer && handleFieldChange) {
      handleFieldChange(ProspectFields.pipeline_prospects__stage, '')
      handleFieldChange(ProspectFields.pipeline_prospects__stage__label, '')
    } else if (!isDrawer && recordId && recordId !== 'new') {
      const clearUpdates = {
        [ProspectFields.pipeline_prospects__stage]: '',
        [ProspectFields.pipeline_prospects__stage__label]: '',
        pipeline_config__id: ''
      }
      dispatch(updateProspectRecord({ prospectId: recordId.toString(), updates: clearUpdates }))
      dispatch(updateActiveRecord({ uuid: recordId.toString(), updates: clearUpdates }))
    }
  }, [
    currentPipelineStageId,
    pipelineStageIdsKey,
    prospectPipelineId,
    fundIdForPipeline,
    pipelineStageOptions.length,
    isDrawer,
    handleFieldChange,
    recordId,
    dispatch,
    pipelineConfigs,
    pipelineStages
  ])

  const fieldOptions = {
    pipelineStageOptions,
    currencyOptions: normalizedCurrencyOptions,
    getCurrencyById,
    getFundOptions: () => normalizedFundOptions,
    getFundOptionsFooter: () => fundOptionsFooter,
    getPipelineStageOptions: () => pipelineStageOptions,
    getCurrencyOptions: () => normalizedCurrencyOptions
  }

  const handlePatchSuccessInternal = async (patchRecordId: string, updates: Record<string, any>) => {
    if (updates[ProspectFields.prospects__fund] !== undefined && patchRecordId) {
      const fundId = normalizeFundIdFromPatch(updates[ProspectFields.prospects__fund])
      const merged: Record<string, any> = {
        ...data,
        ...updates,
        [ProspectFields.prospects__fund]: fundId ?? updates[ProspectFields.prospects__fund]
      }
      const { pipelineOrigination, stageReset, pipelineProspectsRowId, needsPipelineProspectsStagePatch } =
        applyFundChangeToUpdates(updates, merged, {
          fundOptions,
          pipelineConfigs,
          pipelineStages,
          defaultFundraisingPipelineId
        })

      await syncFundChangePipelineOnServer(
        patchRecordId,
        pipelineOrigination,
        needsPipelineProspectsStagePatch,
        stageReset,
        pipelineProspectsRowId,
        () => getAccessToken(store.getState().auth)
      )
    }

    if (updates[ProspectFields.pipeline_prospects__stage]) {
      const pipelineConfigId = updates[ProspectFields.pipeline_prospects__stage]
      updates['pipeline_config__id'] = pipelineConfigId
      const config = pipelineConfigs.find((c: any) => c.id === pipelineConfigId)
      if (config) {
        const stage = pipelineStages.find((s: any) => s.id === config.stage)
        if (stage && stage.name) {
          updates[ProspectFields.pipeline_prospects__stage__label] = stage.name
        }
      }
    }

    const currentToken = getAccessToken(store.getState().auth)
    if (isDrawer && patchRecordId === prospectId && prospectId && currentToken) {
      try {
        const response = await fetchFundraisingById(prospectId, currentToken)

        if (response.data && !response.error) {
          dispatch(updateProspectRecord({ prospectId: prospectId.toString(), updates: response.data }))

          const fetchedProspect = response.data

          const normalizeContacts = (contacts: any): any[] => {
            if (Array.isArray(contacts)) return contacts
            if (contacts) return [contacts]
            return []
          }

          const updatedContacts = {
            introduced_by: normalizeContacts(fetchedProspect[ProspectFields.contact_accounts__introduced_by]),
            sourced_by: normalizeContacts(fetchedProspect[ProspectFields.contact_accounts__sourced_by]),
            lead: normalizeContacts(fetchedProspect[ProspectFields.contact_prospects__lead])
          }

          setPreviousContacts(updatedContacts)

          if (onContactsChange) {
            const transformedContacts = {
              introduced_by: updatedContacts.introduced_by.map((contact: any) => ({
                value: contact[UserEntryFields.contacts__id] || contact.id || '',
                label:
                  contact[UserEntryFields.prog__display_name] ||
                  `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
                  contact[UserEntryFields.contacts__email] ||
                  UNKNOWN_CONTACT,
                email: contact[UserEntryFields.contacts__email] || '',
                data: contact
              })),
              sourced_by: updatedContacts.sourced_by.map((contact: any) => ({
                value: contact[UserEntryFields.contacts__id] || contact.id || '',
                label:
                  contact[UserEntryFields.prog__display_name] ||
                  `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
                  contact[UserEntryFields.contacts__email] ||
                  UNKNOWN_CONTACT,
                email: contact[UserEntryFields.contacts__email] || '',
                data: contact
              })),
              lead: updatedContacts.lead.map((contact: any) => ({
                value: contact[UserEntryFields.contacts__id] || contact.id || '',
                label:
                  contact[UserEntryFields.prog__display_name] ||
                  `${contact[UserEntryFields.contacts__first_name] || ''} ${contact[UserEntryFields.contacts__last_name] || ''}`.trim() ||
                  contact[UserEntryFields.contacts__email] ||
                  UNKNOWN_CONTACT,
                email: contact[UserEntryFields.contacts__email] || '',
                data: contact
              }))
            }
            onContactsChange(transformedContacts)
          }
        }
      } catch (error) {
        console.error('Error fetching prospect after patch:', error)
      }
    }

    if (onPatchSuccess) {
      onPatchSuccess(patchRecordId, updates)
    } else {
      dispatch(updateProspectRecord({ prospectId: patchRecordId, updates }))
    }
  }

  const overviewTeamFields = getOverviewProspectTeamFields(data, selectedContacts, investorLookupId, previousContacts)

  return (
    <FormSection
      data-debug='prospect-overview-section'
      data-component='ProspectOverviewSection'
      data-is-drawer={isDrawer}
      title={SECTION_PROSPECT_OVERVIEW}
      titleColor='blue'
      className='shadow-sm border border-gray-200'
      {...cardProps}
    >
      <div data-debug='prospect-overview-content'>
        {isDrawer ? (
          <div>
            {(() => {
              const allInfoFields = getOverviewProspectInfoFields(data, fieldOptions, isReadOnly)
              const notesField = allInfoFields.find((f) => f.key === ProspectFields.prospects__notes)
              const otherInfoFields = allInfoFields.filter((f) => f.key !== ProspectFields.prospects__notes)

              return (
                <>
                  <Row data-debug='prospect-overview-drawer-layout' gutter={16}>
                    <Col data-debug='prospect-overview-left-column' xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
                      <InputManager
                        inputMode='read'
                        labelPosition='inline'
                        fields={otherInfoFields}
                        onChange={(key, value) => handleFieldChange?.(key, value)}
                        enableAutoPatch={!isReadOnly && !!prospectId}
                        patchId={prospectId}
                        recordId={prospectId}
                        onPatchSuccess={handlePatchSuccessInternal}
                        className='prospect-overview-prospect-info'
                        fieldWrapping={true}
                      />
                    </Col>

                    <Col data-debug='prospect-overview-right-column' xs={24} sm={24} md={24} lg={24} xl={24} xxl={12}>
                      {isDrawer && !isReadOnly && (
                        <>
                          {/* Introduced by / Sourced by belong to the investor:
                              shown read-only here, changed on the investor form. */}
                          <AccountSourcingFields
                            teamFields={overviewTeamFields}
                            account={data?.[ProspectFields.prospects__investor]}
                            accountNoun='investor'
                            inputMode='read'
                            labelPosition='inline'
                            className='prospect-overview-prospect-team-sourcing'
                            fieldWrapping={true}
                          />
                          <InputManager
                            inputMode='read'
                            labelPosition='inline'
                            fields={overviewTeamFields.filter((field) => !isSourcingField(field))}
                            onChange={(key, value) => {
                              if (key === ProspectFields.contact_prospects__lead) {
                                onContactsChange?.({ ...selectedContacts, lead: contactOptionsFromFieldValue(value) })
                              }
                            }}
                            enableAutoPatch={!!prospectId}
                            patchId={prospectId}
                            recordId={prospectId}
                            onPatchSuccess={handlePatchSuccessInternal}
                            className='prospect-overview-prospect-team'
                            fieldWrapping={true}
                          />
                        </>
                      )}
                    </Col>
                  </Row>

                  {notesField && (
                    <Row gutter={16}>
                      <Col xs={24} sm={24} md={24} lg={24} xl={24} xxl={24}>
                        <InputManager
                          inputMode='read'
                          labelPosition='inline'
                          fields={[notesField]}
                          onChange={(key, value) => handleFieldChange?.(key, value)}
                          enableAutoPatch={!isReadOnly && !!prospectId}
                          patchId={prospectId}
                          recordId={prospectId}
                          onPatchSuccess={handlePatchSuccessInternal}
                          className='prospect-overview-prospect-notes'
                          fieldWrapping={true}
                          maxLabelWidth={169}
                        />
                      </Col>
                    </Row>
                  )}
                </>
              )
            })()}
          </div>
        ) : (
          <div data-debug='prospect-overview-single-column' className='prospect-overview-fields'>
            {(() => {
              const allInfoFields = getOverviewProspectInfoFields(data, fieldOptions, isReadOnly)
              const notesField = allInfoFields.find((f) => f.key === ProspectFields.prospects__notes)
              const otherInfoFields = allInfoFields.filter((f) => f.key !== ProspectFields.prospects__notes)

              return (
                <>
                  <InputManager
                    inputMode='read'
                    labelPosition='inline'
                    fields={otherInfoFields}
                    onChange={(key, value) => handleFieldChange?.(key, value)}
                    enableAutoPatch={!isReadOnly && !!prospectId}
                    patchId={prospectId}
                    recordId={prospectId}
                    onPatchSuccess={handlePatchSuccessInternal}
                    className='prospect-overview-single-column'
                    fieldWrapping={true}
                  />
                  {notesField && (
                    <div>
                      <InputManager
                        inputMode='read'
                        labelPosition='inline'
                        fields={[notesField]}
                        onChange={(key, value) => handleFieldChange?.(key, value)}
                        enableAutoPatch={!isReadOnly && !!prospectId}
                        patchId={prospectId}
                        recordId={prospectId}
                        onPatchSuccess={handlePatchSuccessInternal}
                        className='prospect-overview-prospect-notes'
                        fieldWrapping={true}
                        maxLabelWidth={169}
                      />
                    </div>
                  )}
                </>
              )
            })()}
          </div>
        )}
      </div>
    </FormSection>
  )
}
